/**
 * Order error data transformation: parses order API error responses
 * into error table rows and builds order form data for API submission.
 */

import { extractValue } from '@/util/formatting';
import { getEntityFallbackValue } from '@/services/entity-tree';
import type { ErrorCell } from '../utils/fixErrorFieldMaps';
import {
  dashToNull,
  normaliseDateValue,
  getLockboxValue,
  getAccessCodeValue,
  getRentlyValue,
  hasActiveFieldsExcludingOptional,
  resolveNullableValue,
  ORDER_OPTIONAL_FIELDS,
} from '../utils/fixErrorFieldMaps';

// ---- Types ----------------------------------------------------------------

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

// ---- Order error parsing --------------------------------------------------

export const parseOrderErrors = (
  tableData: Record<string, unknown>[]
): { rows: ErrorTableRow[]; failedCount: number } => {
  const rows: ErrorTableRow[] = [];
  let failedCount = 0;

  tableData.forEach((item: Record<string, unknown>, index: number) => {
    const makeCell = (
      field: string,
      opts?: { isOptionalField?: boolean }
    ): ErrorCell => ({
      value: extractValue(item[field] as string | null),
      isActive: false,
      ...(opts?.isOptionalField ? { isOptionalField: true } : {}),
    });

    let services: ErrorCell;
    if (Array.isArray(item.services)) {
      services = {
        value: (item.services as string[]).map(extractValue).join(','),
        isActive: false,
      };
    } else {
      services = { value: extractValue(item.services as string | null), isActive: false };
    }

    const address = makeCell('address');
    const propertyType = makeCell('propertyType');
    const unitNumber = makeCell('unitNumber');
    const city = makeCell('city');
    const state = makeCell('state');
    const zipCode: ErrorCell = { value: extractValue(item.zip as string | null), isActive: false };
    const onsiteContactName = makeCell('onsiteContactName');
    const onsiteContactPhone = makeCell('onsiteContactPhone');
    const accessType = makeCell('accessType');
    const lockboxLocation = makeCell('lockboxLocation');
    const accessCode = makeCell('accessCode');
    const rentlyRegistrationPin: ErrorCell = item.rentlyRegistrationPin
      ? { value: extractValue(item.rentlyRegistrationPin as string | null), isActive: false }
      : { value: '-', isActive: false };
    const entityRaw = extractValue(item.entity as string | null);
    const entity: ErrorCell = {
      value:
        entityRaw === '-' || entityRaw === ''
          ? getEntityFallbackValue()
          : (entityRaw as string),
      isActive: false,
    };
    const orderNotes = makeCell('orderNotes', { isOptionalField: true });
    const gateCode = makeCell('gateCode', { isOptionalField: true });
    const firstAvailableDate = makeCell('firstAvailableDate', { isOptionalField: true });
    const propertyId = makeCell('propertyId', { isOptionalField: true });
    const previousOrderId = makeCell('previousOrderId', { isOptionalField: true });
    const beds = makeCell('beds', { isOptionalField: true });
    const baths = makeCell('baths', { isOptionalField: true });
    const squareFeet = makeCell('squareFeet', { isOptionalField: true });

    const errorReasons = item.errorReasons as string[] | null;

    if (errorReasons) {
      failedCount += 1;
      let checkIsActive = false;

      applyOrderErrorReasons(errorReasons, {
        services, address, propertyType, unitNumber, city, state, zipCode,
        onsiteContactName, onsiteContactPhone, accessType, lockboxLocation,
        accessCode, rentlyRegistrationPin, entity, orderNotes, gateCode,
        firstAvailableDate, propertyId, previousOrderId, beds, baths, squareFeet,
      }, (activated) => { checkIsActive = checkIsActive || activated; });

      if (!checkIsActive) {
        for (const reason of errorReasons) {
          if (reason.includes('Something went wrong, please contact admin')) {
            markAllMandatoryFields({
              services, address, propertyType, unitNumber, city, state,
              zipCode, onsiteContactName, onsiteContactPhone, accessType,
              lockboxLocation, accessCode, rentlyRegistrationPin, entity,
            });
          }
        }
      }
    }

    rows.push({
      id: index + 1,
      fileId: item.fileId as string,
      status: item.status as string,
      orderId: item.orderId as string,
      propertyPrice: item.propertyPrice as number,
      orderSource: item.orderSource as string,
      rowNumber: item.rowNumber as number,
      services, propertyType, address, unitNumber, city, state, zipCode,
      onsiteContactName, onsiteContactPhone, accessType, lockboxLocation,
      accessCode, rentlyRegistrationPin, entity, orderNotes, gateCode,
      firstAvailableDate, propertyId, previousOrderId, beds, baths, squareFeet,
    });
  });

  return { rows, failedCount };
};

// ---- Error reason matching ------------------------------------------------

const MANDATORY_REASON_MAP: Array<{ patterns: string[]; cell: string; message: string }> = [
  { patterns: ['services', 'Service(s)'], cell: 'services', message: 'Not a valid service.' },
  { patterns: ['cannot mix Image Edit'], cell: 'services', message: "AI Virtual Staging (Image Edit) can't be combined with property services — order them separately." },
  { patterns: ['Address', 'address1'], cell: 'address', message: 'Not a valid address.' },
  { patterns: ['Property Type'], cell: 'propertyType', message: 'Not a valid property Type.' },
  { patterns: ['City', 'city'], cell: 'city', message: 'Not a valid city.' },
  { patterns: ['State', 'state'], cell: 'state', message: 'Not a valid state.' },
  { patterns: ['zip code', 'zip', 'Zip', 'zipcode', 'Zip Code'], cell: 'zipCode', message: 'Not a valid zip code.' },
  { patterns: ['On-Site Contact Name'], cell: 'onsiteContactName', message: 'Not a valid on-site contact name.' },
  { patterns: ['On-Site Contact Phone'], cell: 'onsiteContactPhone', message: 'Not a valid on-site contact phone.' },
  { patterns: ['Access Type'], cell: 'accessType', message: 'Not a valid access type.' },
  { patterns: ['Lockbox Location'], cell: 'lockboxLocation', message: 'Not a valid Lockbox location.' },
  { patterns: ['accessCode', 'Access Code'], cell: 'accessCode', message: 'Not a valid access code.' },
  { patterns: ['rentlyRegistrationPin', 'Rently Registration Pin'], cell: 'rentlyRegistrationPin', message: 'Not a valid rently registration pin.' },
  { patterns: ['Entity ID', 'Entity'], cell: 'entity', message: 'Not a valid entity.' },
];

const OPTIONAL_REASON_MAP: Array<{ patterns: string[]; cell: string; message: string }> = [
  { patterns: ['Property Id', 'Property ID'], cell: 'propertyId', message: 'Limit 50 characters' },
  { patterns: ['Previous Order ID'], cell: 'previousOrderId', message: 'Enter a valid Previous Order ID.' },
  { patterns: ['required when ordering an Image Edit'], cell: 'previousOrderId', message: 'Previous Order ID is required for AI Virtual Staging (Image Edit) services.' },
  { patterns: ['only valid for Image Edit'], cell: 'previousOrderId', message: 'Previous Order ID applies only to AI Virtual Staging (Image Edit) services — remove it for property services.' },
  { patterns: ['must be a numeric order ID'], cell: 'previousOrderId', message: 'Enter a valid numeric Previous Order ID.' },
  { patterns: ['orderNotes', 'Order Notes'], cell: 'orderNotes', message: 'Not a valid notes' },
  { patterns: ['gateCode', 'Gate code', 'Gate Code'], cell: 'gateCode', message: 'Not a valid gate code' },
  { patterns: ['firstAvailableDate', 'Schedule Later Date', 'First Available Date', 'First available date'], cell: 'firstAvailableDate', message: 'Enter a valid date' },
  { patterns: ['Bedrooms', 'Beds'], cell: 'beds', message: 'Enter a valid number' },
  { patterns: ['Bathrooms', 'Baths'], cell: 'baths', message: 'Enter a valid number' },
  { patterns: ['Square Feet'], cell: 'squareFeet', message: 'Enter a valid number' },
];

function applyOrderErrorReasons(
  reasons: string[],
  cells: Record<string, ErrorCell>,
  onActivated: (activated: boolean) => void
): void {
  for (const reason of reasons) {
    for (const mapping of MANDATORY_REASON_MAP) {
      if (mapping.patterns.some((p) => reason.includes(p))) {
        const target = cells[mapping.cell];
        if (target) {
          target.isActive = true;
          target.errorMessage = mapping.message;
          onActivated(true);
        }
      }
    }
    for (const mapping of OPTIONAL_REASON_MAP) {
      if (mapping.patterns.some((p) => reason.includes(p))) {
        const target = cells[mapping.cell];
        if (target) {
          target.isActive = true;
          target.errorMessage = mapping.message;
        }
      }
    }
  }
}

function markAllMandatoryFields(cells: Record<string, ErrorCell>): void {
  // `unitNumber` is intentionally absent: backend `FileUploadUtil.validateString`
  // on the order bulk-import path accepts blank for the Apt/Suite/Other column
  // (verified 2026-05-20 against order-service `application.properties` field
  // map + `validateString`). Don't red-flag a field the server treats as optional.
  const msgs: Record<string, string> = {
    services: 'Not a valid service.',
    address: 'Not a valid address.',
    propertyType: 'Not a valid property Type.',
    city: 'Not a valid city.',
    state: 'Not a valid state.',
    zipCode: 'Not a valid zip code.',
    onsiteContactName: 'Not a valid on-site contact name.',
    onsiteContactPhone: 'Not a valid on-site contact phone.',
    accessType: 'Not a valid access type.',
    lockboxLocation: 'Not a valid Lockbox location.',
    accessCode: 'Not a valid access code.',
    rentlyRegistrationPin: 'Not a valid rently registration pin.',
    entity: 'Not a valid entity.',
  };
  for (const [key, message] of Object.entries(msgs)) {
    if (cells[key]) {
      cells[key].isActive = true;
      cells[key].errorMessage = message;
    }
  }
}

// ---- Form data builder ----------------------------------------------------

export const buildOrderFormData = (
  data: ErrorTableRow[],
  errorTableData: ErrorTableRow[]
): Record<string, unknown>[] => {
  let parsedData: ErrorTableRow[];
  if (typeof data === 'string') {
    parsedData = JSON.parse(data);
  } else {
    parsedData = data;
  }

  return parsedData.map((item, index) => {
    const cell = (field: string): ErrorCell | undefined =>
      item[field] as ErrorCell | undefined;
    const origRow = errorTableData[index];

    const servicesValue = cell('services')?.value;
    const servicesData = servicesValue ? servicesValue.split(',') : [];
    const checkMandateFields = hasActiveFieldsExcludingOptional(
      item as Record<string, ErrorCell | unknown>,
      ORDER_OPTIONAL_FIELDS
    );

    return {
      fileId: origRow.fileId,
      orderId: origRow.orderId,
      status: origRow.status,
      firstAvailableDate: resolveNullableValue(
        checkMandateFields,
        cell('firstAvailableDate')?.isActive ?? false,
        normaliseDateValue(cell('firstAvailableDate')?.value)
      ),
      beds: resolveNullableValue(checkMandateFields, cell('beds')?.isActive ?? false, cell('beds')?.value ?? null),
      baths: resolveNullableValue(checkMandateFields, cell('baths')?.isActive ?? false, cell('baths')?.value ?? null),
      squareFeet: resolveNullableValue(checkMandateFields, cell('squareFeet')?.isActive ?? false, cell('squareFeet')?.value ?? null),
      orderNotes: cell('orderNotes')?.value,
      gateCode: dashToNull(cell('gateCode')?.value),
      propertyId: dashToNull(cell('propertyId')?.value),
      previousOrderId: dashToNull(cell('previousOrderId')?.value),
      propertyPrice: origRow.propertyPrice,
      orderSource: origRow.orderSource,
      rowNumber: origRow.rowNumber,
      services: servicesData.join(','),
      propertyType: cell('propertyType')?.value,
      address: dashToNull(cell('address')?.value),
      unitNumber: dashToNull(cell('unitNumber')?.value),
      city: dashToNull(cell('city')?.value),
      state: dashToNull(cell('state')?.value),
      zip: dashToNull(cell('zipCode')?.value),
      entity: (origRow.entity as ErrorCell)?.value,
      onsiteContactName: dashToNull(cell('onsiteContactName')?.value),
      onsiteContactPhone: dashToNull(cell('onsiteContactPhone')?.value),
      accessType: dashToNull(cell('accessType')?.value),
      lockboxLocation: dashToNull(cell('lockboxLocation')?.value)
        ? getLockboxValue(cell('accessType')?.value ?? null, cell('lockboxLocation')?.value ?? null)
        : null,
      accessCode: dashToNull(cell('accessCode')?.value)
        ? getAccessCodeValue(cell('accessType')?.value ?? null, cell('accessCode')?.value ?? null)
        : null,
      rentlyRegistrationPin: dashToNull(cell('rentlyRegistrationPin')?.value)
        ? getRentlyValue(cell('accessType')?.value ?? null, cell('rentlyRegistrationPin')?.value ?? null)
        : null,
    };
  });
};
